import { BetterFetch, BetterFetchOption } from 'better-auth/react';
import { BetterAuthClientPlugin, BetterAuthPlugin } from 'better-auth';
import { createAuthEndpoint } from 'better-auth/plugins';
import { setSessionCookie } from 'better-auth/cookies';
import z from 'zod';

type DevPluginOptions = {
  enabled?: boolean;
};

export const devLogin = (options: DevPluginOptions) => {
  return {
    id: 'dev',
    schema: {
      session: {
        fields: {
          type: {
            type: 'string',
            required: false,
          },
          guildId: {
            type: 'string',
            required: false,
          },
          instanceId: {
            type: 'string',
            required: false,
          },
          channelId: {
            type: 'string',
            required: false,
          },
        },
      },
    },
    endpoints: {
      devSignIn: createAuthEndpoint(
        '/dev/sign-in',
        {
          method: 'POST',
        },
        async (ctx) => {
          if (options.enabled === false) {
            return ctx.json({ ok: false });
          }
          console.log('devSignIn 0: start');
          const { username } = z.object({ username: z.string() }).parse(ctx.body);

          const account = await ctx.context.internalAdapter.findAccountByProviderId(username, 'dev');

          console.log('devSignIn 1: looked up better auth account');

          if (!account) {
            console.log('devSignIn NEW USER 0');
            const user = await ctx.context.internalAdapter.createUser({
              email: `${username}@local.dev`,
              name: username,
            });
            if (!user) return ctx.json({ error: 'Failed to create user' }, { status: 500 });

            console.log('devSignIn NEW USER 1: created user');

            const newAccount = await ctx.context.internalAdapter.createAccount({
              providerId: 'dev',
              userId: user.id,
              accountId: username,
            });
            if (!newAccount) return ctx.json({ error: 'Failed to create account' }, { status: 500 });

            console.log('devSignIn NEW USER 2: created account');

            const session = await ctx.context.internalAdapter.createSession(user.id, ctx, false, {
              type: 'dev',
              guildId: 'dev',
              instanceId: 'dev',
              channelId: 'dev',
            });
            if (!session) return ctx.json({ error: 'Failed to create session' }, { status: 500 });

            console.log('devSignIn NEW USER 3: created session');

            // await setSessionCookie(ctx, { session, user }); // (disabled for now, returning token instead)

            return ctx.json({ ok: true, token: session.token });
          } else {
            console.log('devSignIn EXISTING USER 0');

            const user = await ctx.context.internalAdapter.findUserById(account.userId);
            if (!user) return ctx.json({ error: 'User not found' }, { status: 404 });

            console.log('devSignIn EXISTING USER 1: got user');

            ctx.context.internalAdapter.deleteSessions(user.id);

            console.log('devSignIn EXISTING USER 2: deleted other sessions');

            const session = await ctx.context.internalAdapter.createSession(user.id, ctx, false, {
              type: 'dev',
              guildId: 'dev',
              instanceId: 'dev',
              channelId: 'dev',
            });
            if (!session) return ctx.json({ error: 'Failed to create session' }, { status: 500 });

            console.log('devSignIn EXISTING USER 3: created session');

            // await setSessionCookie(ctx, { session, user }); // (disabled for now, returning token instead)

            console.log('devSignIn EXISTING USER 4: done');

            return ctx.json({ ok: true, token: session.token });
          }
        }
      ),
    },
  } satisfies BetterAuthPlugin;
};

export const devLoginClient = () => {
  return {
    id: 'dev',
    $InferServerPlugin: {} as ReturnType<typeof devLogin>,
    getActions: (fetch, store, options) => {
      return {
        devSignIn: async (args: { username: string }, fetchOptions?: BetterFetchOption) => {
          return await postDevSignIn(args.username, fetch, fetchOptions);
        },
      };
    },
  } satisfies BetterAuthClientPlugin;
};

// // *** helpers ***

async function postDevSignIn(username: string, fetch: BetterFetch, fetchOptions?: BetterFetchOption) {
  const { data, error } = await fetch<{ ok: boolean; token: string }>('/dev/sign-in', {
    method: 'POST',
    body: {
      username,
    },
    ...fetchOptions,
  });
  return data;
}
